import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';
import { toast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';

// ─────────────────────────────────────────────────────────────────────────────
// D.28e — Recurrence Patterns CRUD UI (consumed by SystemMaintenance tab).
// System patterns (is_system=true, company_id=NULL): read-only.
// Custom patterns (is_system=false, company_id=mine): editable + deletable.
// ─────────────────────────────────────────────────────────────────────────────

// NO-UTC: get current local date/time from browser without UTC conversion.
function getLocalNow() {
  const d = new Date();
  const Y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, '0');
  const D = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return { local_date: Y + '-' + M + '-' + D, local_time: h + ':' + m + ':' + s };
}

const VALID_FIELD_TYPES = ['day_picker', 'days_multi', 'select', 'number'];

const EMPTY_FORM = {
  code: '',
  label: '',
  description: '',
  icon: '',
  is_active: true,
  sort_order: 0
};

// Default config_fields for new custom patterns. The admin never sees this —
// patterns added via the UI start empty and are populated programmatically
// when the developer adds support for the pattern in calculateMonthlyDates.
const DEFAULT_CONFIG_FIELDS = { fields: [] };

// D.30e-2: Status computation for a pattern row.
// Returns: 'system' | 'pending_review' | 'active' | 'inactive'
const getPatternStatus = (p) => {
  if (p.is_system) return 'system';
  if (p.is_pending_review || (!p.approved_at && !p.is_active)) return 'pending_review';
  return p.is_active ? 'active' : 'inactive';
};

export const RecurrencePatternsContent = () => {
  const [patterns, setPatterns]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editingPattern, setEditing]  = useState(null);
  const [editingConfigFields, setEditingConfigFields] = useState(null);
  // D.30d: post-save modal state (shown after a NEW custom is created)
  const [postSaveInfo, setPostSaveInfo] = useState(null);  // { pattern, notification_status }
  const [form, setForm]               = useState(EMPTY_FORM);
  const [formErrors, setFormErrors]   = useState({});
  const [submitting, setSubmitting]   = useState(false);
  const [confirmDelete, setConfirmDel] = useState(null);
  // D.30e-2: retract confirmation (separate dialog from regular delete)
  const [confirmRetract, setConfirmRetract] = useState(null);

  const fetchPatterns = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/recurrence-patterns?include_inactive=true');
      const items = (res.data && res.data.items) || [];
      // Sort: globals first (by sort_order), then customs (by sort_order)
      items.sort((a, b) => {
        const aGlobal = a.company_id === null;
        const bGlobal = b.company_id === null;
        if (aGlobal !== bGlobal) return aGlobal ? -1 : 1;
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
      setPatterns(items);
    } catch (err) {
      console.error('Failed to load patterns:', err);
      toast.error('Failed to load recurrence patterns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPatterns(); }, [fetchPatterns]);

  const openCreate = () => {
    setEditing(null);
    setEditingConfigFields(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      code: p.code,
      label: p.label,
      description: p.description || '',
      icon: p.icon || '',
      is_active: p.is_active,
      sort_order: p.sort_order || 0
    });
    // Preserve the original config_fields silently for the update payload.
    // Admin never sees or edits this — only the developer can change it.
    setEditingConfigFields(p.config_fields || DEFAULT_CONFIG_FIELDS);
    setFormErrors({});
    setShowModal(true);
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!editingPattern) {
      const c = (form.code || '').trim();
      if (!c) {
        errors.code = 'Code is required';
      } else if (!/^[a-z0-9_]+$/.test(c)) {
        errors.code = 'Only lowercase letters, digits, and underscores';
      } else if (c.length > 40) {
        errors.code = 'Max 40 characters';
      }
    }
    if (!form.label.trim()) errors.label = 'Label is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const { local_date, local_time } = getLocalNow();
    // config_fields source: defaults for create, preserved value for edit.
    // The admin never sees this — it's a developer concern (Modelo 1 contract).
    const configFields = editingPattern
      ? (editingConfigFields || DEFAULT_CONFIG_FIELDS)
      : DEFAULT_CONFIG_FIELDS;
    const payload = {
      label: form.label.trim(),
      description: form.description.trim() || null,
      icon: form.icon.trim() || null,
      config_fields: configFields,
      is_active: form.is_active,
      sort_order: parseInt(form.sort_order, 10) || 0,
      local_date: local_date,
      local_time: local_time
    };
    if (!editingPattern) payload.code = form.code.trim();

    setSubmitting(true);
    try {
      if (editingPattern) {
        await api.put('/recurrence-patterns/' + editingPattern.id, payload);
        toast.success('Pattern updated');
        setShowModal(false);
        fetchPatterns();
      } else {
        // D.30d: capture response to show the post-save informational modal
        const res = await api.post('/recurrence-patterns', payload);
        const data = (res && res.data) || {};
        setShowModal(false);
        fetchPatterns();
        setPostSaveInfo({
          pattern: data,
          notification_status: data.notification_status || 'sent'
        });
      }
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || 'Save failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // D.30e-2: Retract a Pending Review pattern. Same DELETE endpoint; backend
  // detects approved_at IS NULL and dispatches a retraction email to support
  // before deleting the row.
  const handleRetract = async (p) => {
    const { local_date, local_time } = getLocalNow();
    try {
      await api.delete(
        '/recurrence-patterns/' + p.id +
        '?local_date=' + local_date + '&local_time=' + local_time
      );
      toast.success('Request retracted. SafeCore has been notified.');
      setConfirmRetract(null);
      fetchPatterns();
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || 'Retract failed';
      toast.error(msg);
      setConfirmRetract(null);
    }
  };

  const handleDelete = async (p) => {
    const { local_date, local_time } = getLocalNow();
    try {
      await api.delete(
        '/recurrence-patterns/' + p.id +
        '?local_date=' + local_date + '&local_time=' + local_time
      );
      toast.success('Pattern deleted');
      setConfirmDel(null);
      fetchPatterns();
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || 'Delete failed';
      toast.error(msg);
      setConfirmDel(null);
    }
  };

  const handleToggleActive = async (p) => {
    if (p.is_system) {
      toast.error('System patterns cannot be modified directly. Create a custom override.');
      return;
    }
    const { local_date, local_time } = getLocalNow();
    try {
      await api.put('/recurrence-patterns/' + p.id, {
        is_active: !p.is_active,
        local_date: local_date,
        local_time: local_time
      });
      toast.success(p.is_active ? 'Pattern deactivated' : 'Pattern activated');
      fetchPatterns();
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || 'Failed to toggle';
      toast.error(msg);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Recurrence Patterns</h2>
          <p style={S.subtitle}>
            System patterns are read-only. Create custom patterns to override or add new ones for your company.
          </p>
        </div>
        <button onClick={openCreate} style={S.btnPrimary}>
          + Add Custom Pattern
        </button>
      </div>

      {loading ? (
        <div style={S.loading}>Loading patterns...</div>
      ) : patterns.length === 0 ? (
        <div style={S.empty}>No patterns found.</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Sort</th>
                <th style={S.th}>Icon</th>
                <th style={S.th}>Code</th>
                <th style={S.th}>Label</th>
                <th style={S.th}>Scope</th>
                <th style={S.th}>Active</th>
                <th style={S.th}>Fields</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {patterns.map((p) => {
                const isGlobal = p.company_id === null;
                const fieldCount = (p.config_fields && Array.isArray(p.config_fields.fields))
                  ? p.config_fields.fields.length : 0;
                return (
                  <tr key={p.id} style={p.is_active ? {} : S.rowDeactivated}>
                    <td style={S.td}>{p.sort_order}</td>
                    <td style={S.td}>{p.icon || '–'}</td>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{p.code}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{p.label}</div>
                      {p.description && (
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{p.description}</div>
                      )}
                    </td>
                    <td style={S.td}>
                      <span style={isGlobal ? S.badgeSystem : S.badgeCustom}>
                        {isGlobal ? 'System' : 'Custom'}
                      </span>
                    </td>
                    <td style={S.td}>
                      {(() => {
                        const st = getPatternStatus(p);
                        if (st === 'pending_review') {
                          return <span style={S.badgePending}>⏳ Pending Review</span>;
                        }
                        if (st === 'active' || st === 'system') {
                          return <span style={S.badgeActive}>Active</span>;
                        }
                        return <span style={S.badgeInactive}>Inactive</span>;
                      })()}
                    </td>
                    <td style={S.td}>{fieldCount}</td>
                    <td style={S.td}>
                      {(() => {
                        const st = getPatternStatus(p);
                        if (st === 'system') {
                          return (
                            <span style={{ color: '#999', fontSize: 12, fontStyle: 'italic' }}>
                              read-only
                            </span>
                          );
                        }
                        if (st === 'pending_review') {
                          return (
                            <button
                              onClick={() => setConfirmRetract(p)}
                              style={{ ...S.btnSm, ...S.btnSmDanger }}
                              title="Retract this pattern request"
                            >
                              Retract
                            </button>
                          );
                        }
                        // 'active' or 'inactive': admin has full control over approved customs
                        return (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => openEdit(p)} style={S.btnSm}>Edit</button>
                            <button
                              onClick={() => handleToggleActive(p)}
                              style={{ ...S.btnSm, ...S.btnSmSecondary }}
                            >
                              {p.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => setConfirmDel(p)}
                              style={{ ...S.btnSm, ...S.btnSmDanger }}
                            >
                              Delete
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={S.overlay} onClick={() => !submitting && setShowModal(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h3 style={{ margin: 0 }}>
                {editingPattern ? 'Edit Pattern' : 'Add Custom Pattern'}
              </h3>
              <button
                onClick={() => !submitting && setShowModal(false)}
                style={S.btnClose}
              >×</button>
            </div>

            <div style={S.modalBody}>
              {/* Code (only on create) */}
              {!editingPattern && (
                <div style={S.field}>
                  <label style={S.label}>Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="e.g. every_10_days"
                    style={{ ...S.input, ...(formErrors.code ? S.inputError : {}) }}
                  />
                  <div style={S.help}>
                    Only lowercase letters, digits, and underscores. Cannot be changed later.
                  </div>
                  {formErrors.code && <div style={S.errorText}>{formErrors.code}</div>}
                </div>
              )}

              {editingPattern && (
                <div style={S.field}>
                  <label style={S.label}>Code (read-only)</label>
                  <div style={S.readOnly}>{form.code}</div>
                </div>
              )}

              <div style={S.field}>
                <label style={S.label}>Label *</label>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. Every 10 days"
                  style={{ ...S.input, ...(formErrors.label ? S.inputError : {}) }}
                />
                {formErrors.label && <div style={S.errorText}>{formErrors.label}</div>}
              </div>

              <div style={S.field}>
                <label style={S.label}>Description</label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  style={S.input}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={S.field}>
                  <label style={S.label}>Icon</label>
                  <input
                    type="text"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    placeholder="📅 (emoji or short text)"
                    style={S.input}
                  />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Sort Order</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                    style={S.input}
                  />
                </div>
              </div>

              <div style={{ ...S.field, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <label htmlFor="is_active" style={{ cursor: 'pointer' }}>
                  Active (visible in service-order modal)
                </label>
              </div>
            </div>

            <div style={S.modalFooter}>
              <button
                onClick={() => setShowModal(false)}
                disabled={submitting}
                style={S.btnSecondary}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={S.btnPrimary}
              >
                {submitting ? 'Saving...' : (editingPattern ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* D.30e-2: Retract confirmation (different copy from regular Delete) */}
      {confirmRetract && (
        <ConfirmDialog
          title="Retract Pattern Request"
          message={
            'This will retract your request for the pattern "' + confirmRetract.label + '". ' +
            'SafeCore will be notified by email and the pattern will be removed from your system. ' +
            'You can submit a new request afterward if needed. ' +
            'Are you sure?'
          }
          confirmLabel="Retract"
          confirmStyle="danger"
          onConfirm={() => handleRetract(confirmRetract)}
          onCancel={() => setConfirmRetract(null)}
        />
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete Recurrence Pattern"
          message={
            'Are you sure you want to delete the pattern "' + confirmDelete.label + '"? ' +
            'This cannot be undone. If any active recurring services use this pattern, ' +
            'the deletion will be blocked.'
          }
          confirmLabel="Delete"
          confirmStyle="danger"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDel(null)}
        />
      )}

      {/* D.30d: Post-save informational modal */}
      {postSaveInfo && (
        <PostSaveModal
          pattern={postSaveInfo.pattern}
          notificationStatus={postSaveInfo.notification_status}
          onClose={() => setPostSaveInfo(null)}
        />
      )}
    </div>
  );
};

// ─── Post-save informational modal (D.30d) ──────────────────────────────────
const PostSaveModal = ({ pattern, notificationStatus, onClose }) => {
  const variants = {
    sent: {
      borderColor: '#16A34A',
      headerBg: '#DCFCE7',
      headerColor: '#166534',
      icon: '✓',
      title: 'Pattern Submitted for Review',
      body: (
        <>
          <p style={{ margin: '0 0 12px' }}>
            Your custom pattern <strong>"{pattern.label}"</strong> has been registered
            and will be reviewed by the SafeCore team within <strong>24 hours</strong>.
          </p>
          <p style={{ margin: '0 0 12px' }}>
            Once approved, this pattern will become available in your service order
            modal and you can use it to create recurring contracts with that schedule.
          </p>
          <p style={{ margin: 0 }}>
            We've sent a confirmation to your email and notified our team.
          </p>
        </>
      )
    },
    admin_email_failed: {
      borderColor: '#16A34A',
      headerBg: '#DCFCE7',
      headerColor: '#166534',
      icon: '✓',
      title: 'Pattern Submitted for Review',
      body: (
        <>
          <p style={{ margin: '0 0 12px' }}>
            Your custom pattern <strong>"{pattern.label}"</strong> has been registered
            and will be reviewed by the SafeCore team within <strong>24 hours</strong>.
          </p>
          <p style={{ margin: '0 0 12px' }}>
            Our team has been notified successfully.
          </p>
          <div style={{ padding: 10, background: '#FEF3C7', borderRadius: 4, fontSize: 12, color: '#92400E' }}>
            <strong>Note:</strong> we couldn't deliver a confirmation to your inbox.
            If you'd like a copy, please verify your email address in your profile.
          </div>
        </>
      )
    },
    safecore_failed: {
      borderColor: '#F59E0B',
      headerBg: '#FEF3C7',
      headerColor: '#92400E',
      icon: '⚠',
      title: 'Pattern Submitted — Action Needed',
      body: (
        <>
          <p style={{ margin: '0 0 12px' }}>
            Your custom pattern <strong>"{pattern.label}"</strong> has been registered.
          </p>
          <p style={{ margin: '0 0 12px' }}>
            However, our automatic notification to the review team failed to deliver.
            We've sent you an email with the request details — please{' '}
            <strong>forward that email to info@safecorepest.com</strong> so the team
            can review your pattern.
          </p>
          <p style={{ margin: 0, fontSize: 12, color: '#92400E' }}>
            Check your inbox for an email titled "Action needed: please forward this to support".
          </p>
        </>
      )
    },
    both_failed: {
      borderColor: '#DC2626',
      headerBg: '#FEE2E2',
      headerColor: '#7F1D1D',
      icon: '⚠',
      title: 'Pattern Submitted — Manual Notification Required',
      body: (
        <>
          <p style={{ margin: '0 0 12px' }}>
            Your custom pattern <strong>"{pattern.label}"</strong> has been registered,
            but we couldn't reach the review team via email and we couldn't deliver
            a notification to your inbox either.
          </p>
          <p style={{ margin: '0 0 12px' }}>
            Please email <strong>info@safecorepest.com</strong> directly with these
            details so your request can be reviewed:
          </p>
          <pre style={{
            padding: 10, background: '#F9FAFB', borderRadius: 4,
            fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap',
            border: '1px solid #E5E7EB', margin: 0
          }}>
{`Pattern ID:   #${pattern.id}
Code:         ${pattern.code}
Label:        ${pattern.label}
Description:  ${pattern.description || '(none)'}`}
          </pre>
        </>
      )
    }
  };

  const v = variants[notificationStatus] || variants.sent;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1100
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#fff', borderRadius: 8,
        width: 'min(560px, 95vw)', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        border: '2px solid ' + v.borderColor,
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: '14px 20px',
          backgroundColor: v.headerBg, color: v.headerColor,
          borderRadius: '6px 6px 0 0',
          display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700
        }}>
          <span style={{ fontSize: 24 }}>{v.icon}</span>
          <span style={{ fontSize: 16 }}>{v.title}</span>
        </div>
        <div style={{
          padding: 20, overflowY: 'auto',
          fontSize: 14, color: '#333', lineHeight: 1.5
        }}>
          {v.body}
        </div>
        <div style={{
          padding: '14px 20px',
          borderTop: '1px solid #e5e5e5',
          display: 'flex', justifyContent: 'flex-end'
        }}>
          <button onClick={onClose} style={{
            padding: '9px 22px',
            backgroundColor: '#2D5C3F', color: '#fff',
            border: 'none', borderRadius: 6,
            fontSize: 13, fontWeight: 600, cursor: 'pointer'
          }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Standalone page wrapper (for direct route /recurrence-patterns) ─────────
const RecurrencePatternsPage = () => (
  <Layout>
    <div style={{ padding: 24 }}>
      <RecurrencePatternsContent />
    </div>
  </Layout>
);

export default RecurrencePatternsPage;

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16 },
  title:    { margin: 0, fontSize: 22, fontWeight: 700, color: '#222' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#666' },
  loading:  { padding: 40, textAlign: 'center', color: '#888' },
  empty:    { padding: 40, textAlign: 'center', color: '#888', fontStyle: 'italic' },

  tableWrap: { overflowX: 'auto', border: '1px solid #e5e5e5', borderRadius: 8 },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e5e5', fontSize: 12, fontWeight: 700, color: '#444', backgroundColor: '#fafafa', textTransform: 'uppercase', letterSpacing: 0.5 },
  td:        { padding: '12px', borderBottom: '1px solid #f0f0f0', fontSize: 13, color: '#333', verticalAlign: 'top' },
  rowDeactivated: { opacity: 0.55 },

  badgeSystem:  { display: 'inline-block', padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: '#fff', backgroundColor: '#2D5C3F' },
  badgeCustom:  { display: 'inline-block', padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: '#fff', backgroundColor: '#3B82F6' },
  badgeActive:  { display: 'inline-block', padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: '#166534', backgroundColor: '#DCFCE7' },
  badgeInactive:{ display: 'inline-block', padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: '#7F1D1D', backgroundColor: '#FEE2E2' },
  badgePending: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: '#92400E', backgroundColor: '#FEF3C7', whiteSpace: 'nowrap' },

  btnPrimary:   { padding: '9px 16px', backgroundColor: '#2D5C3F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '9px 16px', backgroundColor: '#fff', color: '#444', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnSm:        { padding: '5px 10px', backgroundColor: '#2D5C3F', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  btnSmSecondary: { backgroundColor: '#6B7280' },
  btnSmDanger:  { backgroundColor: '#DC2626' },
  btnClose:     { background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', padding: 0, lineHeight: 1 },

  overlay:    { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:      { backgroundColor: '#fff', borderRadius: 8, width: 'min(700px, 95vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  modalHeader:{ padding: '16px 20px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalBody:  { padding: 20, overflowY: 'auto' },
  modalFooter:{ padding: '14px 20px', borderTop: '1px solid #e5e5e5', display: 'flex', justifyContent: 'flex-end', gap: 8 },

  field:    { marginBottom: 14 },
  label:    { display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600, color: '#444' },
  input:    { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' },
  inputError:{ borderColor: '#DC2626' },
  readOnly: { padding: '8px 10px', backgroundColor: '#f5f5f5', borderRadius: 4, fontSize: 13, fontFamily: 'monospace', color: '#666' },
  help:     { fontSize: 11, color: '#888', marginTop: 4 },
  errorText:{ fontSize: 11, color: '#DC2626', marginTop: 4, fontWeight: 600 },
};
